import type {
  UserProfile,
  ProfileFormData,
  VitaminsSurvey,
  EngineSurvey,
  VitaminsApiStep,
  VitaminsSurveyResponsesResponse,
  DashboardResponse,
  TenantSettings,
  TenantSettingsUpdate,
  MembershipConsentCreateRequest,
  MembershipConsentRecord,
  ActivePlatformConsent,
  RecommendationResponse,
  DashboardScoresResponse,
  DashboardCategorizedTopics,
  WellbeingWheelData,
  EvolunConfig,
  EvolunConfigRequest,
  EvolunTestConnectionResponse,
  EvolunSyncStatus,
  EvolunSyncResult,
  EvolunConsentSyncResult,
  HrisPlatformInfo,
  HrisConfigRequest,
  HrisConfigResponse,
  HrisTestConnectionResponse,
  HrisSyncTriggerResponse,
  HrisSyncStatusResponse,
  HrisSyncJobListResponse,
  HrisEmployeeListResponse,
  RadarChartData,
  AssistSurveyListResponse,
  AssistEngineSurveyDetail,
  CompanyInsightsResponse,
  InsightsAvailabilityResponse,
  WorkforceSignal,
  PeoplePattern,
  ParticipationAggregate,
  InsightActionsResponse,
  InsightActionsByTeamResponse,
  ActionTrackingUpdate,
  ActionTrackingResponse,
  InsightFilterDimension,
  InsightFilterFacets,
  DriversMatrixResponse,
  GlossaryResponse,
  CategoricalEvolutionResponse,
  ComparableSurveysResponse,
  ComplianceReport,
  ComplianceReportConfig,
  ComplianceReportType,
  ComplianceReportsResponse,
  DriversEvolutionResponse,
  InsightSnapshotResponse,
  TeamRiskResponse,
  TeamDetailResponse,
  TeamCorrelationsResponse,
  CorrelationMatrixResponse,
  PageNarrativeResponse,
  SegmentsResponse,
  QuadrantMapResponse,
  FilterSelection,
  HomePreferences,
  UpdateHomePreferencesPayload,
} from '../types';
import type {
  TenantContext as TenantContextDoc,
  DimensionValues,
} from '../components/assist/admin/context/types';
import type { RangeKey, TrendsResponse } from '../features/selfcare/trends/types';

const API_BASE = '/api';

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
    /**
     * Server-authored copy that is safe to render verbatim to an end user.
     *
     * Only set when the response body carried a deliberate, human-written
     * message (see `curatedUserMessage`). `message` stays technical and is
     * for logs — never render it. Call sites should go through
     * `toUserMessage()` in `lib/errors.ts` rather than reading either field.
     */
    public userMessage?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Generic HTTP status phrases. FastAPI and gateways emit these as `detail`
 * when nobody wrote real copy, so they carry no more meaning than the status
 * code we already have — and read as technical noise to a user.
 */
const GENERIC_HTTP_PHRASES = new Set([
  'bad request',
  'unauthorized',
  'forbidden',
  'not found',
  'method not allowed',
  'conflict',
  'unprocessable entity',
  'too many requests',
  'internal server error',
  'bad gateway',
  'service unavailable',
  'gateway timeout',
]);

/**
 * Decide whether a server-supplied string is deliberate copy we can show a
 * user, as opposed to raw internals that happened to land in the body.
 *
 * Deliberately conservative — when in doubt we return undefined and the caller
 * falls back to our own translated copy. Nothing from a 5xx is ever trusted:
 * those messages describe our failures, not anything the user can act on.
 */
function curatedUserMessage(status: number, raw: unknown): string | undefined {
  if (status >= 500) return undefined;
  if (typeof raw !== 'string') return undefined;

  const text = raw.trim();
  if (!text) return undefined;

  // Deliberate copy is a single sentence. Stack traces, HTML error pages and
  // JSON dumps are long, marked up, or span lines.
  if (text.length > 160) return undefined;
  if (/[\r\n]/.test(text)) return undefined;
  if (/[<>{}]|\bTraceback\b|\b[A-Za-z]*Error:/.test(text)) return undefined;
  // A URL in an error message is an internal hostname or endpoint, not
  // something we wrote for a user to read.
  if (/https?:\/\//i.test(text)) return undefined;
  // A bare number is a status or error code with the label stripped off.
  if (/^\d+$/.test(text)) return undefined;
  if (GENERIC_HTTP_PHRASES.has(text.toLowerCase())) return undefined;

  return text;
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers: HeadersInit = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Always send httpOnly cookies
  });

  if (!response.ok) {
    // Session expired or invalid — destroy session and redirect to login
    if (response.status === 401) {
      // Don't redirect during setup wizard — unauthenticated API calls are expected
      if (window.location.pathname.startsWith('/setup')) {
        throw new ApiError(401, 'Not authenticated');
      }
      localStorage.removeItem('session');
      localStorage.removeItem('elli-current-tenant-id');
      localStorage.removeItem('elli-user-profile');
      localStorage.removeItem('elli-sidebar-collapsed');
      window.location.href = '/';
      throw new ApiError(401, 'Session expired');
    }

    const errorText = await response.text();
    let errorMessage = errorText;
    let errorBody: unknown = undefined;
    // Stays undefined unless the body carried deliberate human copy. A
    // non-JSON body (nginx HTML, a gateway text page) never sets it.
    let userMessage: string | undefined;

    try {
      const errorData = JSON.parse(errorText);
      errorBody = errorData;
      // FastAPI returns `detail` as a string OR a validation-error array
      // (`[{loc, msg, type}, ...]`). Flatten to a readable message instead of
      // letting an array/object stringify to "[object Object]".
      const detail = (errorData as { detail?: unknown }).detail;
      if (Array.isArray(detail)) {
        // Pydantic validation output — field paths and type names. Readable
        // enough for a log, never for a user.
        errorMessage = detail
          .map((d) =>
            typeof d === 'string' ? d : (d as { msg?: string })?.msg ?? JSON.stringify(d),
          )
          .join('; ');
      } else if (typeof detail === 'string') {
        errorMessage = detail;
        userMessage = curatedUserMessage(response.status, detail);
      } else if (detail && typeof detail === 'object') {
        errorMessage = (detail as { msg?: string }).msg ?? JSON.stringify(detail);
      } else {
        const bffError = (errorData as { error?: string }).error;
        errorMessage = bffError || errorText;
        userMessage = curatedUserMessage(response.status, bffError);
      }
    } catch {
      // If not JSON, use text as-is
    }

    throw new ApiError(response.status, errorMessage, errorBody, userMessage);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const api = {
  get: <T>(path: string): Promise<T> => {
    return request<T>(path, { method: 'GET', cache: 'no-store' });
  },

  post: <T>(path: string, data: unknown): Promise<T> => {
    return request<T>(path, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  patch: <T>(path: string, data: unknown): Promise<T> => {
    return request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  put: <T>(path: string, data: unknown): Promise<T> => {
    return request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: (path: string): Promise<void> => {
    return request<void>(path, { method: 'DELETE' });
  },

  upload: async <T>(path: string, formData: FormData): Promise<T> => {
    const url = `${API_BASE}${path}`;

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = errorText;

      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorData.detail || errorText;
      } catch {
        // If not JSON, use text as-is
      }

      throw new ApiError(response.status, errorMessage);
    }

    return response.json();
  },
};

// Profile API functions
export const getProfile = (): Promise<UserProfile> => {
  return api.get<UserProfile>('/users/me/profile');
};

/**
 * Update current user's profile
 * Converts ProfileFormData (strings) to API payload (numbers for height/weight)
 * Accepts partial data - only provided fields will be updated
 * Empty strings are sent as null to clear optional fields
 */
export const updateProfile = (data: Partial<ProfileFormData>): Promise<UserProfile> => {
  const payload: Record<string, unknown> = {};

  // Required fields - only include if present and non-empty
  if (data.first_name) payload.first_name = data.first_name;
  if (data.last_name) payload.last_name = data.last_name;

  // Optional string fields - send null to clear, value to set
  if (data.phone !== undefined) {
    payload.phone = data.phone || null;
  }
  if (data.preferred_language !== undefined) {
    payload.preferred_language = data.preferred_language || null;
  }
  if (data.date_of_birth !== undefined) {
    payload.date_of_birth = data.date_of_birth || null;
  }
  if (data.gender !== undefined) {
    payload.gender = data.gender || null;
  }

  // Optional numeric fields - send null to clear, parsed number to set
  if (data.height_cm !== undefined) {
    payload.height_cm = data.height_cm ? parseFloat(data.height_cm) : null;
  }
  if (data.weight_kg !== undefined) {
    payload.weight_kg = data.weight_kg ? parseFloat(data.weight_kg) : null;
  }

  return api.patch<UserProfile>('/users/me/profile', payload);
};

/**
 * Get the current user's home-page preferences (favourites, dismissed
 * sections, recently viewed), scoped server-side to the caller's current
 * tenant. A user with nothing saved yet gets a valid empty default, not 404.
 */
export const getHomePreferences = (): Promise<HomePreferences> => {
  return api.get<HomePreferences>('/users/me/home-preferences');
};

/**
 * Partial-update the current user's home-page preferences. `favourites`/
 * `dismissed` are shallow-merged server-side (include a key to set it, omit
 * to leave it untouched); `recently_viewed_key` records a visit, capped
 * server-side.
 */
export const updateHomePreferences = (
  payload: UpdateHomePreferencesPayload,
): Promise<HomePreferences> => {
  return api.patch<HomePreferences>('/users/me/home-preferences', payload);
};

// Self-Care Wellbeing API functions (Dashboard Components)

/**
 * Get available self-care surveys
 *
 * Returns either:
 * - EngineSurvey[] when survey engine is active (has user_state field)
 * - VitaminsSurvey[] when using legacy vitamins mode
 *
 * The caller should use isEngineSurvey() type guard to determine the format.
 */
export const getSelfCareSurveys = (locale: string = 'nl'): Promise<(VitaminsSurvey | EngineSurvey)[]> => {
  return api.get<(VitaminsSurvey | EngineSurvey)[]>(`/self-care/surveys?locale=${encodeURIComponent(locale)}`);
};

/**
 * Get survey steps/questions
 */
export const getSurveySteps = (surveyId: string, locale: string = 'nl'): Promise<VitaminsApiStep[]> => {
  return api.get<VitaminsApiStep[]>(`/self-care/survey/${encodeURIComponent(surveyId)}/steps?locale=${encodeURIComponent(locale)}`);
};

/**
 * Get survey submissions (user's answers)
 */
export const getSurveySubmissions = (surveyId: string, locale: string = 'nl'): Promise<any> => {
  return api.get(`/self-care/survey/${encodeURIComponent(surveyId)}/submissions?locale=${encodeURIComponent(locale)}`);
};

/**
 * Get formatted survey responses for "Your Responses" view
 * Returns question/answer pairs with factor grouping, aggregated server-side
 * @param responseId - Required for survey engine mode (submission ID)
 */
export const getSurveyResponses = (surveyId: string, locale: string = 'nl', responseId?: string): Promise<VitaminsSurveyResponsesResponse> => {
  const params = new URLSearchParams({ locale });
  if (responseId) {
    params.append('responseId', responseId);
  }
  return api.get<VitaminsSurveyResponsesResponse>(`/self-care/survey/${encodeURIComponent(surveyId)}/responses?${params.toString()}`);
};

/**
 * Get wellbeing wheel result for a specific survey
 */
export const getSurveyResult = (surveyId: string, submissionId: string, locale: string = 'nl'): Promise<WellbeingWheelData> => {
  return api.get<WellbeingWheelData>(`/self-care/survey/${encodeURIComponent(surveyId)}/result?submission=${encodeURIComponent(submissionId)}&locale=${encodeURIComponent(locale)}`);
};

/**
 * Get radar chart data for survey engine results
 * Returns module-aggregated scores for dynamic radar chart visualization
 * Only available for survey engine surveys (not vitamins)
 */
export const getEngineRadarResult = (surveyId: string, language: string = 'en'): Promise<RadarChartData> => {
  return api.get<RadarChartData>(`/self-care/engine/survey/${encodeURIComponent(surveyId)}/radar-result?language=${encodeURIComponent(language)}`);
};

/**
 * Get combined survey results across all completed surveys
 * Returns module-aggregated scores for the self-care dashboard
 */
export const getCombinedSurveyResults = (language: string = 'en'): Promise<RadarChartData> =>
  api.get<RadarChartData>(`/self-care/engine/all-results?language=${encodeURIComponent(language)}`);

/**
 * Latest completed survey for the current user (or null).
 *
 * Used by the readiness hero in S4 (between-pulses) to show the most
 * recent survey title + overall score even when no pulse is active.
 */
export interface LatestCompletedSurvey {
  surveyId: string;
  surveyName: Record<string, string>;
  overallScorePct: number;
  completedAt: string;
}

export const getLatestCompletedSurvey = (language: string = 'en'): Promise<LatestCompletedSurvey | null> =>
  api.get<LatestCompletedSurvey | null>(`/self-care/engine/latest-completed?language=${encodeURIComponent(language)}`);

/**
 * Create a new survey submission (start the survey)
 * This is called when no submission exists yet and the user starts answering
 * Returns the new submission data with submission ID
 */
export const createSurveySubmission = (surveyId: string, locale: string = 'nl'): Promise<any> => {
  return api.post(`/self-care/survey/${encodeURIComponent(surveyId)}/submissions?locale=${encodeURIComponent(locale)}`, {});
};

/**
 * Update survey submission (save user's answers)
 * This is called progressively as the user navigates through survey steps
 */
export const updateSurveySubmission = (
  surveyId: string,
  submissionId: string,
  responseData: Record<string, string>,
  locale: string = 'nl',
  comments: string = ''
): Promise<any> => {
  // Group answers by question type prefix (DEM, HES, etc.) and strip prefix from keys
  // Example: { "HES-FDFRQ1": "0.5" } becomes { HES: { "FDFRQ1": "0.5" } }
  const groupedData: Record<string, Record<string, string>> = {};

  for (const [key, value] of Object.entries(responseData)) {
    // Extract prefix (e.g., "HES" from "HES-FDFRQ1")
    const match = key.match(/^([A-Z]+)-(.+)$/);
    if (match) {
      const [, prefix, questionKey] = match;
      if (!groupedData[prefix]) {
        groupedData[prefix] = {};
      }
      groupedData[prefix][questionKey] = value;
    } else {
      // If no prefix found, put in HES group as fallback
      if (!groupedData.HES) {
        groupedData.HES = {};
      }
      groupedData.HES[key] = value;
    }
  }

  const payload = {
    checkup: surveyId,
    response_data: groupedData,
    language: locale,
    comments,
    shared_with: [],
  };

  return api.patch(
    `/self-care/survey/${encodeURIComponent(surveyId)}/submissions/${encodeURIComponent(submissionId)}?locale=${encodeURIComponent(locale)}`,
    payload
  );
};

/**
 * Publish survey submission (mark as complete)
 * Called after all questions are answered to make results visible
 *
 * For survey engine mode: POST to /publish endpoint which calls backend complete API
 * For Vitamins mode: BFF falls back to PATCH on submission
 */
export const publishSurvey = (
  surveyId: string,
  submissionId: string,
  locale: string = 'nl'
): Promise<any> => {
  // POST to the publish endpoint - survey engine uses this to call complete API
  return api.post(
    `/self-care/survey/${encodeURIComponent(surveyId)}/publish?locale=${encodeURIComponent(locale)}`,
    { submissionId }
  );
};

// Dashboard API functions

/**
 * Get dashboard data including configured survey
 * Returns single survey (configured or first available as fallback)
 */
export const getDashboard = (locale: string = 'nl'): Promise<DashboardResponse> => {
  return api.get<DashboardResponse>(`/dashboard?locale=${encodeURIComponent(locale)}`);
};

/**
 * Get tenant settings
 */
export const getDashboardSettings = (): Promise<TenantSettings> => {
  return api.get<TenantSettings>('/dashboard/settings');
};

/**
 * Update tenant settings
 */
export const updateDashboardSettings = (data: TenantSettingsUpdate): Promise<TenantSettings> => {
  return api.patch<TenantSettings>('/dashboard/settings', data);
};

// Membership Consent API functions

/**
 * Get all active platform consents (all consent types)
 * Returns an array of active consent definitions for platform_consent, privacy_policy, and terms_of_use
 */
export const getActiveConsents = (): Promise<ActivePlatformConsent[]> => {
  return api.get<ActivePlatformConsent[]>('/consent/active');
};

/**
 * Get active platform consent (single type)
 * @deprecated Use getActiveConsents() instead to fetch all consent types
 * This now returns the first consent in the array for backwards compatibility
 */
export const getActiveConsent = async (): Promise<ActivePlatformConsent> => {
  const consents = await getActiveConsents();
  return consents[0];
};

/**
 * Get platform consent by type (for public legal document viewer)
 * Used to view the active consent text for a specific consent type
 */
export const getActiveConsentByType = (consentType: string): Promise<ActivePlatformConsent> => {
  return api.get<ActivePlatformConsent>(`/consent/active/${encodeURIComponent(consentType)}`);
};

/**
 * Get platform consent by version and type
 * Used to view the consent text that a user previously accepted
 */
export const getConsentByVersion = (version: string, consentType?: string): Promise<ActivePlatformConsent> => {
  const params = consentType ? `?consent_type=${encodeURIComponent(consentType)}` : '';
  return api.get<ActivePlatformConsent>(`/consent/${encodeURIComponent(version)}${params}`);
};

/**
 * Create a consent record
 */
export const createMembershipConsent = (data: MembershipConsentCreateRequest): Promise<MembershipConsentRecord> => {
  return api.post<MembershipConsentRecord>('/consent', data);
};

/**
 * Get all consent records for current user
 */
export const getMembershipConsents = (): Promise<MembershipConsentRecord[]> => {
  return api.get<MembershipConsentRecord[]>('/consent');
};

/**
 * Withdraw a consent (GDPR Article 7 - right to withdraw)
 * Soft-deletes the consent by setting withdrawn_at timestamp
 */
export interface WithdrawConsentResponse {
  consentType: string;
  withdrawnAt: string;
  message: string;
}

export const withdrawConsent = async (consentType: string): Promise<WithdrawConsentResponse> => {
  const url = `${API_BASE}/consent/${encodeURIComponent(consentType)}`;
  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = errorText;
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.error || errorData.detail || errorText;
    } catch {
      // If not JSON, use text as-is
    }
    throw new ApiError(response.status, errorMessage);
  }

  return response.json();
};

/**
 * Decline a consent (user chooses to continue without consenting)
 * Records the user's decision to decline so they won't be re-prompted
 */
export const declineConsent = (data: MembershipConsentCreateRequest): Promise<MembershipConsentRecord> => {
  return api.post<MembershipConsentRecord>('/consent/decline', data);
};

/**
 * Accept a consent (new consent or after previous decline/withdrawal)
 * Allows giving consent from the profile page after initially declining
 */
export const acceptConsent = (data: MembershipConsentCreateRequest): Promise<MembershipConsentRecord> => {
  return api.post<MembershipConsentRecord>('/consent/accept', data);
};

// Recommendation API functions (under self-care routes)

/**
 * Get user's active recommendation in the specified language
 * If the recommendation exists in a different language, it will be translated automatically
 */
export const getRecommendations = (language: string = 'en'): Promise<RecommendationResponse[]> => {
  return api.get<RecommendationResponse[]>(`/self-care/recommendations?locale=${encodeURIComponent(language)}`);
};

/**
 * Recommendation snapshot — same shape as the active recommendation, but
 * resolved for the recommendation generated FROM the survey at `at` (the
 * survey's completed_at). The BFF returns the earliest COMPLETED
 * recommendation whose created_at is >= `at`. Returns 404 when no
 * recommendation existed for that survey moment — callers should treat
 * that as an "empty" state, not an error.
 *
 * Single object (not array). The BFF returns the same envelope as
 * /api/self-care/recommendations[0].
 */
export const getRecommendationSnapshot = (
  at: string,
  locale: string = 'en',
): Promise<RecommendationResponse> => {
  return api.get<RecommendationResponse>(
    `/self-care/recommendations/snapshot?at=${encodeURIComponent(at)}&locale=${encodeURIComponent(locale)}`,
  );
};

// SelfCare Trends API functions

/**
 * Get SelfCare trends data for the current user
 * Returns time-series data for modules and groups across completed surveys
 * @param locale - Locale for translated labels (e.g., "en", "nl", "fr")
 * @param range - Time range filter ("1m" | "3m" | "6m" | "all"); defaults to "all"
 */
export const getTrends = (
  locale: string,
  range: RangeKey = 'all',
): Promise<TrendsResponse> => {
  return api.get<TrendsResponse>(
    `/self-care/trends?locale=${encodeURIComponent(locale)}&range=${encodeURIComponent(range)}`,
  );
};

// Assist Survey API functions

/** Get assist engine surveys for the current tenant */
export const getAssistSurveys = (): Promise<AssistSurveyListResponse> => {
  return api.get<AssistSurveyListResponse>('/assist/eng-surveys');
};

/** Get a single assist engine survey by ID */
export const getAssistSurvey = (surveyId: string): Promise<AssistEngineSurveyDetail> => {
  return api.get<AssistEngineSurveyDetail>(`/assist/eng-surveys/${encodeURIComponent(surveyId)}`);
};

export interface UpdateAssistSurveyData {
  name?: Record<string, string>;
  descriptions?: Record<string, Record<string, string>> | null;
  module_ids?: string[];
  disabled_question_keys?: string[];
  participant_targeting?: {
    type: 'all' | 'metadata' | 'user_list';
    metadata_filters?: Array<Record<string, unknown>>;
    user_ids?: string[];
  };
  start_date?: string;
  end_date?: string;
  anonymous_responses?: boolean;
  reminder_schedule?: number[];
  reminder_dates?: string[];
  custom_invitation_subject?: Record<string, string> | null;
  custom_invitation_body?: Record<string, string> | null;
  custom_reminder_subject?: Record<string, string> | null;
  custom_reminder_body?: Record<string, string> | null;
}

/** Update a survey (partial — only changed fields) */
export const updateAssistSurvey = (surveyId: string, data: UpdateAssistSurveyData): Promise<AssistEngineSurveyDetail> => {
  return api.put<AssistEngineSurveyDetail>(`/assist/eng-surveys/${encodeURIComponent(surveyId)}`, data);
};

// Assist Survey Wizard API functions

/** Get available platform modules for survey creation */
export const getAssistModules = (): Promise<{
  modules: Array<{
    id: string;
    name: Record<string, string>;
    description: string;
    descriptions: {
      public_employee?: Record<string, string>;
      public_employer?: Record<string, string>;
    } | null;
    questionCount: number;
    validation_status: 'academically_validated' | 'market_standard' | null;
    source_instrument: string | null;
    module_type: 'kpi' | 'driver' | 'compliance_safety' | null;
  }>;
  total: number;
}> => {
  return api.get('/assist/eng-surveys/modules');
};

/** Get platform survey templates for survey creation wizard */
export const getAssistTemplates = (): Promise<{
  templates: Array<{
    id: string;
    name: Record<string, string>;
    descriptions: { public_employee?: Record<string, string>; public_employer?: Record<string, string> } | null;
    sourceModuleIds: string[];
    moduleCount: number;
    questionCount: number;
    validationStatus: TemplateValidationStatus;
    sourceUrl: string | null;
    sourceLabel: string | null;
    domain: string | null;
  }>;
}> => {
  return api.get('/assist/eng-surveys/templates');
};

/** Get available metadata fields for audience targeting */
export const getAssistUserMetadataFields = (): Promise<{
  tenant_id: string;
  fields: Array<{
    key: string;
    label: string;
    source: 'profile' | 'metadata';
    is_array: boolean;
    values: string[];
    count: number;
  }>;
}> => {
  return api.get('/assist/users/metadata-fields');
};

/** Targeting payload accepted by the preview-count endpoint */
export interface PreviewTargetingPayload {
  type: 'all' | 'metadata' | 'user_list';
  metadata_filters?: Array<{
    field: string;
    operator: 'in' | 'contains_any';
    value: string[];
  }>;
  user_ids?: string[];
}

/** Preview the count of users that match a targeting payload (debounced from UI) */
export const previewTargetingCount = (
  participant_targeting: PreviewTargetingPayload,
): Promise<{
  count: number;
  below_min_group_size: boolean;
  min_group_size: number;
}> => {
  return api.post('/assist/users/count-by-targeting', { participant_targeting });
};

/** Get total user count for tenant */
export const getAssistUserCount = (): Promise<{ total: number }> => {
  return api.get<{ total: number }>('/assist/users/count');
};

/** Create survey from modules and optionally publish */
export const createAssistSurvey = (data: {
  module_ids: string[];
  name: Record<string, string>;
  descriptions?: { public_employee?: Record<string, string>; public_employer?: Record<string, string> } | null;
  participant_targeting: {
    type: 'all' | 'metadata' | 'user_list';
    metadata_filters?: Array<{ field: string; operator: 'in' | 'contains_any'; value: string[] }>;
    user_ids?: string[];
  };
  start_date: string | null;
  end_date: string | null;
  publish: boolean;
  launch_now?: boolean;
  reminder_schedule?: number[];
  reminder_dates?: string[];
  custom_invitation_subject?: Record<string, string> | null;
  custom_invitation_body?: Record<string, string> | null;
}): Promise<any> => {
  return api.post('/assist/eng-surveys', data);
};

/** Duplicate an existing survey */
export const duplicateAssistSurvey = (
  surveyId: string,
  data: { name: Record<string, string>; start_date: string | null; end_date: string | null; publish?: boolean; launch_now?: boolean }
): Promise<AssistEngineSurveyDetail> => {
  return api.post<AssistEngineSurveyDetail>(`/assist/eng-surveys/${encodeURIComponent(surveyId)}/duplicate`, data);
};

/** Translate survey invitation text using AI */
export interface TranslateInvitationRequest {
  sourceLang: string;
  targetLang: string;
  subject: string;
  body: string;
  feedback?: string;
}

export interface TranslateInvitationResponse {
  subject: string;
  body: string;
}

export const translateInvitation = (data: TranslateInvitationRequest): Promise<TranslateInvitationResponse> => {
  return api.post<TranslateInvitationResponse>('/assist/eng-surveys/translate-invitation', data);
};

/** Generate AI employee-facing survey name from template */
export const generateSurveyName = (
  templateName: Record<string, string>,
  templateDescription: Record<string, string>,
): Promise<{ name: Record<string, string> }> => {
  return api.post<{ name: Record<string, string> }>('/assist/eng-surveys/generate-name', {
    templateName,
    templateDescription,
  });
};

/** Resend survey invitations to non-responders */
export const resendSurveyInvitations = (surveyId: string): Promise<{ sent: number; failed: number; total_targeted: number }> => {
  return api.post(`/assist/eng-surveys/${encodeURIComponent(surveyId)}/resend-invitations`, {});
};

/** Send reminder emails to non-responders */
export const sendSurveyReminders = (surveyId: string): Promise<{ sent: number; failed: number; total_targeted: number }> => {
  return api.post(`/assist/eng-surveys/${encodeURIComponent(surveyId)}/send-reminders`, {});
};

/** Get survey invitation status */
export const getSurveyInvitationStatus = (surveyId: string): Promise<{
  total_tokens: number;
  total_sent: number;
  total_opened: number;
  total_completed: number;
  batches: unknown[];
}> => {
  return api.get(`/assist/eng-surveys/${encodeURIComponent(surveyId)}/invitation-status`);
};

/** Per-survey participant entry with token + completion state. */
export type SurveyParticipantEntry = {
  user_id: string;
  // 'scheduled' rows are live-evaluated for a published survey that hasn't
  // materialized its participant collection yet (EL-790) — no token/sent data.
  status: 'invited' | 'pending' | 'revoked' | 'scheduled' | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  preferred_language: string | null;
  has_token: boolean;
  invitation_sent_at: string | null;
  last_reminder_sent_at: string | null;
  first_accessed_at: string | null;
  completed: boolean;
  had_response: boolean;
  revoked_reason: string | null;
};

export type NarrowingDecision =
  | 'drop_pending_only'
  | 'revoke_non_responders'
  | 'revoke_all_keep_responses'
  | 'keep_all';

/** List eligible participants for a survey, with per-user invite status. */
export const getSurveyParticipants = (
  surveyId: string,
): Promise<{ participants: SurveyParticipantEntry[] }> => {
  return api.get(`/assist/eng-surveys/${encodeURIComponent(surveyId)}/participants`);
};

/** Send (or resend) the invite email for a single participant. */
export const sendSurveyInvitationToUser = (
  surveyId: string,
  userId: string,
): Promise<{ status: 'sent' | 'skipped' | 'failed'; reason?: string }> => {
  return api.post(
    `/assist/eng-surveys/${encodeURIComponent(surveyId)}/participants/${encodeURIComponent(userId)}/send-invitation`,
    {},
  );
};

/** Toggle auto-invite of post-launch matches (draft/published/active only). */
export const setSurveyAutoInvite = (
  surveyId: string,
  autoInvite: boolean,
): Promise<{ auto_invite_new_matches: boolean; pending_invited: number }> => {
  return api.patch(
    `/assist/eng-surveys/${encodeURIComponent(surveyId)}/auto-invite`,
    { auto_invite_new_matches: autoInvite },
  );
};

/** Bulk-invite users currently in the Pending bucket. */
export const bulkInvitePending = (
  surveyId: string,
  body: { user_ids?: string[]; all_pending?: boolean },
): Promise<{ invited: string[]; failed: Array<{ user_id: string; reason: string }> }> => {
  return api.post(
    `/assist/eng-surveys/${encodeURIComponent(surveyId)}/participants/invite`,
    body,
  );
};

/** Preview the impact of a targeting change before applying it. */
export const previewTargetingEdit = (
  surveyId: string,
  participant_targeting: unknown,
): Promise<{ will_add: number; will_drop: number; dropped_responders: number }> => {
  return api.post(
    `/assist/eng-surveys/${encodeURIComponent(surveyId)}/targeting/preview`,
    { participant_targeting },
  );
};

/** Apply a targeting change with the chosen reconciliation decision. */
export const updateSurveyTargeting = (
  surveyId: string,
  body: { participant_targeting: unknown; narrowing_decision: NarrowingDecision },
): Promise<{
  pending_revoked: string[];
  invited_revoked: string[];
  newly_pending: string[];
}> => {
  return api.put(
    `/assist/eng-surveys/${encodeURIComponent(surveyId)}/targeting`,
    body,
  );
};

/** Delete a draft or scheduled survey */
export const deleteAssistSurvey = (surveyId: string): Promise<void> => {
  return api.delete(`/assist/eng-surveys/${encodeURIComponent(surveyId)}`);
};

/** Stop an active survey immediately (archives it) */
export const stopAssistSurvey = (surveyId: string): Promise<void> => {
  return api.post(`/assist/eng-surveys/${encodeURIComponent(surveyId)}/stop`, {});
};

/** Archive a completed survey */
export const archiveAssistSurvey = (surveyId: string): Promise<void> => {
  return api.post(`/assist/eng-surveys/${encodeURIComponent(surveyId)}/archive`, {});
};

// Dashboard Insights API functions

/**
 * Get transformed dashboard scores from insights service
 * Returns domains with topics, scores, and distributions
 */
export const getDashboardScores = (surveyId: string, locale: string = 'nl'): Promise<DashboardScoresResponse> => {
  return api.get<DashboardScoresResponse>(
    `/dashboard/scores/${encodeURIComponent(surveyId)}?locale=${encodeURIComponent(locale)}`
  );
};

/**
 * Get topics categorized by score threshold
 * - action_required: score < 5
 * - monitoring: 5 <= score < 7
 * - on_track: score >= 7
 */
export const getDashboardCategories = (surveyId: string, locale: string = 'nl'): Promise<DashboardCategorizedTopics> => {
  return api.get<DashboardCategorizedTopics>(
    `/dashboard/scores/${encodeURIComponent(surveyId)}/categories?locale=${encodeURIComponent(locale)}`
  );
};

// Evoluno API functions

/**
 * Get Evoluno configuration for the current tenant
 */
export const getEvolunoConfig = (): Promise<EvolunConfig> => {
  return api.get<EvolunConfig>('/evoluno/config');
};

/**
 * Create or update Evoluno configuration
 */
export const upsertEvolunoConfig = (data: EvolunConfigRequest): Promise<EvolunConfig> => {
  return api.put<EvolunConfig>('/evoluno/config', data);
};

/**
 * Test Evoluno API connection
 */
export const testEvolunoConnection = (): Promise<EvolunTestConnectionResponse> => {
  return api.post<EvolunTestConnectionResponse>('/evoluno/test', {});
};

/**
 * Get Evoluno sync status
 */
export const getEvolunoSyncStatus = (): Promise<EvolunSyncStatus> => {
  return api.get<EvolunSyncStatus>('/evoluno/sync-status');
};

/**
 * Force sync all users to Evoluno
 */
export const syncEvolunoUsers = (): Promise<EvolunSyncResult> => {
  return api.post<EvolunSyncResult>('/evoluno/sync', {});
};

/**
 * Sync all consents to Evoluno
 */
export const syncEvolunoConsentsAll = (): Promise<EvolunConsentSyncResult> => {
  return api.post<EvolunConsentSyncResult>('/evoluno/consent/sync-all', {});
};

// HRIS Integration API functions

/** Get list of supported HRIS platforms */
export const getHrisPlatforms = (): Promise<HrisPlatformInfo[]> => {
  return api.get<HrisPlatformInfo[]>('/hris/platforms');
};

/** Get HRIS connection configuration */
export const getHrisConfig = (): Promise<HrisConfigResponse> => {
  return api.get<HrisConfigResponse>('/hris/config');
};

/** Create or update HRIS connection configuration */
export const upsertHrisConfig = (data: HrisConfigRequest): Promise<HrisConfigResponse> => {
  return api.put<HrisConfigResponse>('/hris/config', data);
};

/** Delete HRIS connection configuration */
export const deleteHrisConfig = (): Promise<void> => {
  return api.delete('/hris/config');
};

/** Test HRIS connection */
export const testHrisConnection = (): Promise<HrisTestConnectionResponse> => {
  return api.post<HrisTestConnectionResponse>('/hris/test', {});
};

/** Trigger HRIS sync */
export const triggerHrisSync = (): Promise<HrisSyncTriggerResponse> => {
  return api.post<HrisSyncTriggerResponse>('/hris/sync', {});
};

/** Get HRIS sync status */
export const getHrisSyncStatus = (): Promise<HrisSyncStatusResponse> => {
  return api.get<HrisSyncStatusResponse>('/hris/sync-status');
};

/** Get HRIS sync job history */
export const getHrisSyncJobs = (page: number = 1, pageSize: number = 20): Promise<HrisSyncJobListResponse> => {
  return api.get<HrisSyncJobListResponse>(`/hris/sync-jobs?page=${page}&page_size=${pageSize}`);
};

/** Get cached HRIS employees */
export const getHrisEmployees = (page: number = 1, pageSize: number = 50): Promise<HrisEmployeeListResponse> => {
  return api.get<HrisEmployeeListResponse>(`/hris/employees?page=${page}&page_size=${pageSize}`);
};

/** Initiate HRIS OAuth flow - returns authorize URL */
export const initiateHrisOAuth = (platform: string): Promise<{ authorize_url: string }> => {
  return api.post<{ authorize_url: string }>('/hris/oauth/authorize', { platform });
};

// Insights Dashboard API functions

/**
 * Get company-wide insight scores from survey aggregation engine
 * Returns module scores, overall score, respondent count, and threshold status
 */
export const getInsightScores = (
  filterField?: string,
  filterValue?: string,
  surveyId?: string,
): Promise<CompanyInsightsResponse> => {
  const params = new URLSearchParams();
  if (filterField) params.append('filter_field', filterField);
  if (filterValue) params.append('filter_value', filterValue);
  if (surveyId) params.append('survey_id', surveyId);
  const qs = params.toString();
  return api.get<CompanyInsightsResponse>(`/insights/scores${qs ? `?${qs}` : ''}`);
};

/**
 * Cheap "do insight results exist yet" check for the sidebar's quick gating.
 *
 * A single backend point-read (no aggregation) — use this instead of
 * getInsightScores() when you only need has_results / module-type flags, so
 * the sidebar menu un-grays without waiting on the full company aggregation.
 *
 * @param surveyId - Read THAT survey's own per-survey doc instead of the
 *   tenant's composed default. Pages that pin a survey (`?survey=`) MUST pass
 *   it — the driver/KPI shape differs per survey, and the default's shape is
 *   what made a pinned survey explain itself with another survey's reason
 *   (EL-1135). Prefer the `useSurveyAvailability` hook over calling this
 *   directly: it handles the no-pin case without a second request and
 *   discards responses for a survey the reader has already moved off.
 */
export const getInsightsAvailability = (
  surveyId?: string,
): Promise<InsightsAvailabilityResponse> => {
  const qs = surveyId ? `?survey_id=${encodeURIComponent(surveyId)}` : '';
  return api.get<InsightsAvailabilityResponse>(`/insights/availability${qs}`);
};

/**
 * Get the drivers heatmap matrix: drivers × group_by column values per domain.
 *
 * The BFF's whitelist parser keeps repeated `filter[<field>]=<value>` segments
 * verbatim, so we emit one segment per selected value (no comma separation).
 *
 * @param groupBy - Metadata field used for column buckets (e.g. "team"). If
 *                  omitted the backend falls back to the first metadata field.
 * @param filters - AND-combined metadata filters keyed by field name.
 */
export const getDriversMatrix = (
  groupBy?: string,
  filters?: FilterSelection,
  compareWith?: string,
  surveyId?: string,
): Promise<DriversMatrixResponse> => {
  const segments: string[] = [];
  if (groupBy) {
    segments.push(`group_by=${encodeURIComponent(groupBy)}`);
  }
  if (compareWith) {
    segments.push(`compare_with=${encodeURIComponent(compareWith)}`);
  }
  if (surveyId) {
    segments.push(`survey_id=${encodeURIComponent(surveyId)}`);
  }
  if (filters) {
    for (const [field, values] of Object.entries(filters)) {
      if (!values || values.length === 0) continue;
      const key = `filter[${encodeURIComponent(field)}]`;
      for (const value of values) {
        segments.push(`${key}=${encodeURIComponent(value)}`);
      }
    }
  }
  const qs = segments.join('&');
  return api.get<DriversMatrixResponse>(`/insights/modules${qs ? `?${qs}` : ''}`);
};

export interface ModulesExportOptions {
  format: 'xlsx' | 'csv';
  groupBy?: string;
  filters?: FilterSelection;
  compareWith?: string;
  includeFreeText?: boolean;
  lang?: string;
}

/** Fetch the modules export and trigger a browser download. */
export const downloadModulesExport = async (
  opts: ModulesExportOptions,
): Promise<void> => {
  const segments: string[] = [`format=${opts.format}`];
  if (opts.groupBy) segments.push(`group_by=${encodeURIComponent(opts.groupBy)}`);
  if (opts.compareWith)
    segments.push(`compare_with=${encodeURIComponent(opts.compareWith)}`);
  if (opts.includeFreeText) segments.push('include_free_text=true');
  if (opts.lang) segments.push(`lang=${encodeURIComponent(opts.lang)}`);
  if (opts.filters) {
    for (const [field, values] of Object.entries(opts.filters)) {
      if (!values || values.length === 0) continue;
      const key = `filter[${encodeURIComponent(field)}]`;
      for (const value of values) segments.push(`${key}=${encodeURIComponent(value)}`);
    }
  }
  const qs = segments.join('&');

  const response = await fetch(`${API_BASE}/insights/modules/export?${qs}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Export failed: ${response.status}`);
  }
  const blob = await response.blob();
  const ext = opts.format === 'csv' ? 'zip' : 'xlsx';
  const date = new Date().toISOString().split('T')[0];
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `elli-modules-export-${date}.${ext}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Get the list of comparable surveys for the current tenant — used to populate
 * the comparison selector on the drivers heatmap (previous / baseline / specific
 * surveys).
 */
export const getComparableSurveys = (): Promise<ComparableSurveysResponse> => {
  return api.get<ComparableSurveysResponse>('/insights/modules/comparable-surveys');
};

/**
 * Get the drivers evolution time series — one point per completed survey per
 * driver, grouped by domain (module). Used to render the Evolution view on the
 * drivers heatmap.
 */
export const getDriversEvolution = (
  filter?: FilterSelection,
): Promise<DriversEvolutionResponse> => {
  const segments: string[] = [];
  if (filter) {
    for (const [field, values] of Object.entries(filter)) {
      if (!values || values.length === 0) continue;
      const key = `filter[${encodeURIComponent(field)}]`;
      for (const value of values) {
        segments.push(`${key}=${encodeURIComponent(value)}`);
      }
    }
  }
  const qs = segments.join('&');
  return api.get<DriversEvolutionResponse>(
    `/insights/modules/evolution${qs ? `?${qs}` : ''}`,
  );
};

/**
 * Get the per-question time series for non-scored (categorical) follow-up
 * questions, used by the stacked-area chart in the Evolution view. Tenant-
 * wide in v1: no filters or group_by are accepted.
 */
export const getCategoricalEvolution = (): Promise<CategoricalEvolutionResponse> => {
  return api.get<CategoricalEvolutionResponse>(
    '/insights/drivers/categorical-evolution',
  );
};

/**
 * Get workforce signals — prioritized issues ranked by severity and trend
 * @param limit - Maximum number of signals to return (default: 10)
 */
export const getWorkforceSignals = (limit: number = 10): Promise<WorkforceSignal[]> => {
  return api.get<WorkforceSignal[]>(`/insights/signals?limit=${limit}`);
};

/**
 * Get pre-computed people patterns for the signals page
 * Returns AI-generated cross-sectional patterns from the nightly clustering job
 */
export const getPeoplePatterns = (): Promise<PeoplePattern[]> => {
  return api.get<PeoplePattern[]>('/insights/patterns');
};

/**
 * Get team comparison — company insights broken down by metadata field
 * @param groupBy - Metadata field to group by (default: 'department')
 */
export const getTeamsComparison = (groupBy: string = 'department'): Promise<Record<string, CompanyInsightsResponse>> => {
  return api.get<Record<string, CompanyInsightsResponse>>(`/insights/teams?group_by=${encodeURIComponent(groupBy)}`);
};

/**
 * Get pre-computed team risk summary with severity classification
 * Returns teams with top risk module, severity levels, and AI summary
 * @param groupBy - Metadata field to group by (default: 'department')
 */
export const getTeamRiskSummary = (groupBy: string = 'department'): Promise<TeamRiskResponse> => {
  return api.get<TeamRiskResponse>(`/insights/teams/risk?group_by=${encodeURIComponent(groupBy)}`);
};

/**
 * Get team detail — full context including member count, survey date, and module insights
 * @param groupBy - Metadata field (e.g., 'department', 'location')
 * @param teamValue - Filter value (e.g., 'Engineering', 'Brussels')
 */
export const getTeamDetail = (
  groupBy: string,
  teamValue: string,
  surveyId?: string,
): Promise<TeamDetailResponse> =>
  api.get<TeamDetailResponse>(
    `/insights/teams/${encodeURIComponent(groupBy)}/${encodeURIComponent(teamValue)}` +
      (surveyId ? `?survey_id=${encodeURIComponent(surveyId)}` : ''),
  );

/**
 * Get team-scoped KPI-driver correlations
 * @param groupBy - Metadata field (e.g., 'department', 'location')
 * @param teamValue - Filter value (e.g., 'Engineering', 'Brussels')
 */
export const getTeamCorrelations = (
  groupBy: string,
  teamValue: string,
  surveyId?: string,
): Promise<TeamCorrelationsResponse> =>
  api.get<TeamCorrelationsResponse>(
    `/insights/teams/${encodeURIComponent(groupBy)}/${encodeURIComponent(teamValue)}/correlations` +
      (surveyId ? `?survey_id=${encodeURIComponent(surveyId)}` : ''),
  );

/**
 * Get company-wide (or optionally scoped) KPI–driver correlations.
 * @param scope - "overall" for tenant-wide (default), or "{dimension}_{value}" for a team
 */
export const getCorrelations = (
  scope?: string,
): Promise<TeamCorrelationsResponse> => {
  const params = scope ? `?scope=${encodeURIComponent(scope)}` : '';
  return api.get<TeamCorrelationsResponse>(`/insights/correlations${params}`);
};

/**
 * Get the dense driver x KPI correlation matrix at question-group grain (EL-722).
 *
 * A sibling of {@link getCorrelations}, not a mode of it: that endpoint rolls the
 * same stored data up to module grain and drops the driver/KPI scores.
 *
 * @param scope - "overall" (default) or "{dimension}_{value}"
 * @param surveyId - defaults server-side to the tenant's latest completed survey
 */
/**
 * Workforce segments for one survey (EL-722).
 *
 * The BFF forwards a 204 when the survey cannot be segmented or its
 * segmentation is below the quality floor. `api.get` yields undefined for a
 * 204, so the caller distinguishes "nothing to show" from an error.
 */
/**
 * Workforce segments. Without a survey id the backend serves the most recent
 * wave that has renderable segments — which is what "Current state" means for
 * this page, and is not the same as the tenant's most recent survey.
 */
export const getSegments = (surveyId?: string): Promise<SegmentsResponse | undefined> =>
  api.get<SegmentsResponse | undefined>(
    surveyId
      ? `/insights/segments?survey_id=${encodeURIComponent(surveyId)}`
      : '/insights/segments',
  );

export const getCorrelationsMatrix = (
  scope?: string,
  surveyId?: string,
): Promise<CorrelationMatrixResponse> => {
  const params = new URLSearchParams();
  if (scope) params.append('scope', scope);
  if (surveyId) params.append('survey_id', surveyId);
  const qs = params.toString();
  return api.get<CorrelationMatrixResponse>(
    `/insights/correlations/matrix${qs ? `?${qs}` : ''}`,
  );
};

/**
 * Get the score x leverage quadrant map for every driver (EL-722).
 *
 * @param scope - "overall" (default) or "{dimension}_{value}"
 * @param surveyId - defaults server-side to the latest completed survey
 * @param kpiGroupKey - switch the y-axis to that KPI's |r|; omit for the
 *   combined shared-leverage lens (which matches the actions engine exactly)
 */
export const getQuadrantMap = (
  scope?: string,
  surveyId?: string,
  kpiGroupKey?: string,
): Promise<QuadrantMapResponse> => {
  const params = new URLSearchParams();
  if (scope) params.append('scope', scope);
  if (surveyId) params.append('survey_id', surveyId);
  if (kpiGroupKey) params.append('kpi_group_key', kpiGroupKey);
  const qs = params.toString();
  return api.get<QuadrantMapResponse>(`/insights/quadrant${qs ? `?${qs}` : ''}`);
};

/**
 * Get the "Elli analysis" paragraph for one page view (EL-730).
 *
 * @param page - which page's band to read, e.g. "correlations"
 * @param surveyId - defaults server-side to the same survey the page resolves
 * @param scope - "overall" (default) or "{dimension}_{value}", same namespace
 *   as /insights/correlations/matrix
 * @param selections - the reader's page-level narrowing, e.g.
 *   `{ kpi_group: ['engagement'] }`; sent as a JSON string, never parsed here
 * @param language - locale for the paragraph, e.g. "en" | "nl" | "fr"
 */
export const getPageNarrative = (
  page: string,
  surveyId?: string,
  scope?: string,
  selections?: Record<string, string[]>,
  language?: string,
): Promise<PageNarrativeResponse> => {
  const params = new URLSearchParams();
  params.append('page', page);
  if (scope) params.append('scope', scope);
  if (surveyId) params.append('survey_id', surveyId);
  if (selections && Object.keys(selections).length > 0) {
    params.append('selections', JSON.stringify(selections));
  }
  if (language) params.append('language', language);
  return api.get<PageNarrativeResponse>(`/insights/narrative?${params.toString()}`);
};

/**
 * Get team-scoped actions — reuses the existing /insights/actions endpoint with team filters
 * @param language - Language for AI-generated text
 * @param groupBy - Metadata field (e.g., 'department')
 * @param teamValue - Filter value (e.g., 'Engineering')
 */
export const getTeamActions = (
  language: string,
  groupBy: string,
  teamValue: string,
): Promise<InsightActionsResponse> =>
  getInsightActions(language, groupBy, teamValue);

/**
 * Get aggregate participation data from latest monthly snapshot.
 * Optionally scoped by metadata filter (e.g. team=Engineering).
 */
export const getParticipationAggregate = (
  filterField?: string,
  filterValue?: string,
  surveyId?: string,
): Promise<ParticipationAggregate> => {
  const params = new URLSearchParams();
  if (filterField) params.append('filter_field', filterField);
  if (filterValue) params.append('filter_value', filterValue);
  if (surveyId) params.append('survey_id', surveyId);
  const qs = params.toString();
  return api.get<ParticipationAggregate>(`/insights/participation${qs ? `?${qs}` : ''}`);
};

/**
 * Get AI-generated insight actions with work tracking and trends
 * @param language - Language for AI-generated text (default: "en")
 */
export const getInsightActions = (
  language?: string,
  filterField?: string,
  filterValue?: string,
  includeHidden?: boolean,
): Promise<InsightActionsResponse> => {
  const params = new URLSearchParams();
  if (language) params.append('language', language);
  if (filterField) params.append('filter_field', filterField);
  if (filterValue) params.append('filter_value', filterValue);
  // Only ever sent as the literal 'true'. The BACKEND decides whether to honour
  // it — admitted for callers holding `action-visibility update`, silently
  // ignored otherwise (EL-1064) — so asking is safe for any role and a
  // client-side role check here would be a second, driftable copy of that rule.
  if (includeHidden) params.append('include_hidden', 'true');
  const qs = params.toString();
  return api.get<InsightActionsResponse>(`/insights/actions${qs ? `?${qs}` : ''}`);
};

/**
 * Set this tenant's own visibility override on one action card (EL-1064).
 *
 * `true` promotes a card the platform-admin consultant suppressed, `false` hides
 * one they published, `null` clears the override and hands the card back to the
 * consultant's own `show_to_tenant`.
 *
 * The override lives in its own field, so a later curation pass can still tell
 * "the consultant published this" from "the tenant un-hid it" instead of
 * silently reversing the tenant's choice. Skeleton ids contain dots and, for
 * segment rows, an embedded `field=value` tail, so the id is URL-encoded.
 */
export const setActionTenantVisibility = (
  skeletonId: string,
  override: boolean | null,
): Promise<unknown> => {
  return api.put(
    `/insights/actions/skeleton/${encodeURIComponent(skeletonId)}/tenant-visibility`,
    { tenant_visibility_override: override },
  );
};

/**
 * Get every team's action cards in one response (EL-617).
 * Powers the actions page's per-team dropdown counts and the all-teams
 * "Team view".
 * @param language - Language for AI-generated text (default: "en")
 */
export const getInsightActionsByTeam = (
  language?: string,
): Promise<InsightActionsByTeamResponse> => {
  const params = new URLSearchParams();
  if (language) params.append('language', language);
  const qs = params.toString();
  return api.get<InsightActionsByTeamResponse>(`/insights/actions/by-team${qs ? `?${qs}` : ''}`);
};

/**
 * Get action tracking state for a specific skeleton (fast — Firestore only, no AI)
 * @param skeletonId - Skeleton ID to get tracking for
 */
export const getActionTrackingBySkeleton = (skeletonId: string): Promise<ActionTrackingResponse> => {
  return api.get<ActionTrackingResponse>(`/insights/actions/skeleton/${encodeURIComponent(skeletonId)}/tracking`);
};

/**
 * Update action tracking state for a specific skeleton
 * @param skeletonId - Skeleton ID to update tracking for
 * @param data - Tracking update data
 */
export const updateActionTrackingBySkeleton = (skeletonId: string, data: ActionTrackingUpdate): Promise<ActionTrackingResponse> => {
  return api.put<ActionTrackingResponse>(`/insights/actions/skeleton/${encodeURIComponent(skeletonId)}/tracking`, data);
};

/**
 * Fetch team members for the current tenant (for assignee selection).
 * Returns active users with their display names and roles.
 */
export const fetchTeamMembers = async (): Promise<Array<{
  user_id: string;
  display_name: string;
  email: string;
  roles: string[];
  avatar_url: string | null;
}>> => {
  const response = await api.get<{
    items: Array<{
      id: string;
      primary_email: string;
      first_name: string | null;
      last_name: string | null;
      memberships: Array<{
        roles: string[];
        status: string;
      }>;
    }>;
  }>('/dashboard/users?page=1&page_size=100&status=active');

  return response.items.map((user) => {
    const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.primary_email;
    const membership = user.memberships[0]; // Current tenant's membership
    return {
      user_id: user.id,
      display_name: displayName,
      email: user.primary_email,
      roles: membership?.roles ?? [],
      avatar_url: null,
    };
  });
};

/**
 * Fetch active members of a single team, identified by the insights group_by
 * dimension (department / location / job_title) and the team's value for that
 * dimension. Reuses /dashboard/users — same source as the admin page.
 *
 * Returns names + emails only; no individual scores or severities.
 */
export const fetchTeamMembersByGroup = async (
  groupBy: 'department' | 'location' | 'job_title',
  value: string,
): Promise<Array<{
  user_id: string;
  display_name: string;
  email: string;
}>> => {
  const params = new URLSearchParams({
    page: '1',
    page_size: '100',
    status: 'active',
  });
  params.append(groupBy, value);

  const response = await api.get<{
    items: Array<{
      id: string;
      primary_email: string;
      first_name: string | null;
      last_name: string | null;
    }>;
  }>(`/dashboard/users?${params.toString()}`);

  return response.items.map((user) => ({
    user_id: user.id,
    display_name:
      [user.first_name, user.last_name].filter(Boolean).join(' ') || user.primary_email,
    email: user.primary_email,
  }));
};

/** One existing member of a team, for rehydrating the onboarding manual builder. */
export interface OnboardingExistingMember {
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * An existing team plus its already-imported members, for restoring the
 * onboarding manual-entry builder on back-navigation (EL-808). `parentName`
 * is the display-only reports-into link (existing hierarchy is already
 * persisted server-side and is never resubmitted).
 */
export interface OnboardingExistingTeam {
  name: string;
  leadName: string;
  leadEmail: string;
  parentName: string | null;
  members: OnboardingExistingMember[];
}

interface OnboardingTeamNode {
  name: string;
  lead_name: string | null;
  lead_email: string | null;
  children: OnboardingTeamNode[];
}

interface OnboardingUserListItem {
  first_name: string | null;
  last_name: string | null;
  primary_email: string;
  memberships: Array<{ department: string | null }>;
}

/**
 * Read the tenant's already-imported teams and people so the onboarding
 * manual builder can be rehydrated on back-navigation (EL-808). Reuses the
 * existing read paths — no new backend endpoint:
 *  - `GET /api/teams` for the team tree (names, hierarchy, lead).
 *  - `GET /api/dashboard/users?status=active` for members, grouped by each
 *    membership's `department` (the field the people-import writes the team
 *    into). Paged to cover tenants past the 100-row page cap.
 *
 * The team's lead is de-duplicated out of its member rows so the builder
 * shows the lead once (in the lead field) and each other person once.
 * Departments that carry members but have no Team doc (e.g. a CSV-upload
 * tenant that never persisted a hierarchy) surface as flat top-level teams,
 * so nobody is silently dropped.
 */
export const fetchOnboardingTeamsWithMembers = async (): Promise<OnboardingExistingTeam[]> => {
  const [teamTree, members] = await Promise.all([
    api
      .get<{ teams: OnboardingTeamNode[] }>('/teams')
      .then((resp) => resp?.teams ?? [])
      .catch(() => [] as OnboardingTeamNode[]),
    fetchActiveMembersWithDepartment(),
  ]);

  const membersByDept = new Map<string, OnboardingExistingMember[]>();
  for (const member of members) {
    if (!member.department) continue;
    const bucket = membersByDept.get(member.department);
    if (bucket) bucket.push(member.person);
    else membersByDept.set(member.department, [member.person]);
  }

  const teams: OnboardingExistingTeam[] = [];
  const emitted = new Set<string>();
  const walk = (nodes: OnboardingTeamNode[], parentName: string | null) => {
    for (const node of nodes) {
      const leadEmail = (node.lead_email ?? '').trim();
      const leadLower = leadEmail.toLowerCase();
      const members = (membersByDept.get(node.name) ?? []).filter(
        (m) => !leadLower || m.email.toLowerCase() !== leadLower,
      );
      teams.push({
        name: node.name,
        leadName: (node.lead_name ?? '').trim(),
        leadEmail,
        parentName,
        members,
      });
      emitted.add(node.name);
      walk(node.children, node.name);
    }
  };
  walk(teamTree, null);

  // Departments with members but no Team doc → flat top-level teams.
  for (const [dept, deptMembers] of membersByDept) {
    if (emitted.has(dept)) continue;
    teams.push({ name: dept, leadName: '', leadEmail: '', parentName: null, members: deptMembers });
  }

  return teams;
};

/** Page through active members, projecting each to person + tenant department. */
const fetchActiveMembersWithDepartment = async (): Promise<
  Array<{ person: OnboardingExistingMember; department: string | null }>
> => {
  const out: Array<{ person: OnboardingExistingMember; department: string | null }> = [];
  const pageSize = 100;
  for (let page = 1; ; page += 1) {
    const resp = await api
      .get<{ items: OnboardingUserListItem[]; total_pages?: number }>(
        `/dashboard/users?page=${page}&page_size=${pageSize}&status=active`,
      )
      .catch(() => null);
    const items = resp?.items ?? [];
    for (const user of items) {
      out.push({
        person: {
          firstName: user.first_name ?? '',
          lastName: user.last_name ?? '',
          email: user.primary_email,
        },
        department: user.memberships?.[0]?.department ?? null,
      });
    }
    const totalPages = resp?.total_pages ?? 1;
    if (items.length === 0 || page >= totalPages) break;
  }
  return out;
};

/**
 * One active member for the onboarding Audience step's "add specific people"
 * picker (EL-809). Carries name + email for display and a normalized `facets`
 * map so the picker can resolve the facet portion of a mixed custom group into
 * concrete ids client-side (a survey targeting is a `metadata` filter OR an
 * explicit `user_list`, never their union).
 */
export interface OnboardingAudienceMember {
  userId: string;
  displayName: string;
  email: string;
  /**
   * Facet values keyed by metadata-field key (canonical + tenant-custom), each
   * a `string[]` so scalar (`in`) and array (`contains_any`) matching both
   * collapse to a set intersection. Keys mirror the user-service metadata-field
   * discovery: `department`, `location`, `job_title`, `cost_center`, `team`,
   * `tags`, plus any auto-detected `profile.metadata` keys.
   */
  facets: Record<string, string[]>;
}

interface OnboardingAudienceMembershipItem {
  department?: string | null;
  jobTitle?: string | null;
  location?: string | null;
  costCenter?: string | null;
  team?: string | null;
  tags?: string[] | null;
  metadata?: Record<string, unknown> | null;
}

interface OnboardingAudienceUserItem {
  id: string;
  primary_email: string;
  first_name: string | null;
  last_name: string | null;
  memberships: OnboardingAudienceMembershipItem[];
}

/** Canonical metadata-field key → the camelCase membership field it maps to. */
const CANONICAL_FACET_FIELDS: Array<[key: string, prop: keyof OnboardingAudienceMembershipItem]> = [
  ['department', 'department'],
  ['location', 'location'],
  ['job_title', 'jobTitle'],
  ['cost_center', 'costCenter'],
  ['team', 'team'],
  ['tags', 'tags'],
];

/** Normalize any facet raw value (scalar, number, or array) into a string[]. */
const toFacetValues = (raw: unknown): string[] => {
  if (raw == null) return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr.map((v) => String(v)).filter((v) => v.length > 0);
};

const normalizeMemberFacets = (m: OnboardingAudienceMembershipItem): Record<string, string[]> => {
  const facets: Record<string, string[]> = {};
  for (const [key, prop] of CANONICAL_FACET_FIELDS) {
    const values = toFacetValues(m[prop]);
    if (values.length > 0) facets[key] = values;
  }
  for (const [key, raw] of Object.entries(m.metadata ?? {})) {
    const values = toFacetValues(raw);
    if (values.length > 0) facets[key] = values;
  }
  return facets;
};

/**
 * Page through the tenant's active members for the onboarding Audience
 * "add specific people" picker (EL-809). Reuses `GET /api/dashboard/users`
 * (same read as the admin people page and the manual-builder rehydration) —
 * no new backend endpoint. Members are returned sorted by display name.
 */
export const fetchOnboardingAudienceMembers = async (): Promise<OnboardingAudienceMember[]> => {
  const out: OnboardingAudienceMember[] = [];
  const pageSize = 100;
  for (let page = 1; ; page += 1) {
    const resp = await api
      .get<{ items: OnboardingAudienceUserItem[]; total_pages?: number }>(
        `/dashboard/users?page=${page}&page_size=${pageSize}&status=active`,
      )
      .catch(() => null);
    const items = resp?.items ?? [];
    for (const user of items) {
      const displayName =
        [user.first_name, user.last_name].filter(Boolean).join(' ') || user.primary_email;
      out.push({
        userId: user.id,
        displayName,
        email: user.primary_email,
        facets: normalizeMemberFacets(user.memberships?.[0] ?? {}),
      });
    }
    const totalPages = resp?.total_pages ?? 1;
    if (items.length === 0 || page >= totalPages) break;
  }
  out.sort((a, b) => a.displayName.localeCompare(b.displayName));
  return out;
};

/**
 * Get available filter dimensions for insights (e.g., department, location).
 *
 * When `applied` is provided, the backend cross-filters each dimension's
 * values against all OTHER applied filters — values that no longer match
 * any respondent given the current selection are dropped, so the dropdowns
 * can't show unselectable options. The dimension's own filter is excluded
 * server-side, so a user can still un-tick values they've already selected.
 */
export const getInsightFilters = (
  applied?: FilterSelection,
): Promise<InsightFilterDimension[]> => {
  const segments: string[] = [];
  if (applied) {
    for (const [field, values] of Object.entries(applied)) {
      if (!values || values.length === 0) continue;
      const key = `filter[${encodeURIComponent(field)}]`;
      for (const value of values) {
        segments.push(`${key}=${encodeURIComponent(value)}`);
      }
    }
  }
  const qs = segments.join('&');
  return api.get<InsightFilterDimension[]>(`/insights/filters${qs ? `?${qs}` : ''}`);
};

/**
 * Get the cross-filter "interlinking" payload (contingency table) for the
 * insights filter bar. Fetched once per dashboard load; the frontend
 * recomputes every dropdown's available values + counts from this single
 * payload as the selection changes — no per-change round-trip.
 */
export const getInsightFacets = (): Promise<InsightFilterFacets> => {
  return api.get<InsightFilterFacets>('/insights/filter-facets');
};

/**
 * Get historical KPI snapshots for trend chart rendering
 * @param scope - "company" or "metadata:{field}:{value}" (default: "company")
 * @param limit - Max snapshots to return (default: 6)
 */
export const getInsightHistory = (
  scope: string = 'company',
  limit: number = 6
): Promise<InsightSnapshotResponse[]> => {
  const params = new URLSearchParams();
  if (scope !== 'company') params.append('scope', scope);
  params.append('limit', String(limit));
  return api.get<InsightSnapshotResponse[]>(`/insights/history?${params.toString()}`);
};

// PLG Signup API functions

export interface SignupRequest {
  company_name: string;
  admin_email: string;
  admin_first_name: string;
  admin_last_name: string;
  accepted_tos: boolean;
  language: string;
}

export interface SignupResponse {
  message: string;
  tenant_id: string;
}

/**
 * Register a new company and admin account (PLG signup).
 * Sends a verification email to the admin.
 */
export const signup = (data: SignupRequest): Promise<SignupResponse> => {
  return api.post<SignupResponse>('/session/signup', data);
};

// Aggregate Participation API functions

// Setup wizard API functions

import type {
  RegisterSetupRequest,
  RegisterSetupResponse,
  LaunchSetupRequest,
  LaunchSetupResponse,
  CreateDraftSurveyRequest,
  CreateDraftSurveyResponse,
  VerifyAndLaunchRequest,
  VerifyAndLaunchResponse,
  SetupResumeResponse,
  SkipAndVerifyRequest,
  SkipAndVerifyResponse,
  SendVerificationRequest,
  SendVerificationResponse,
  VerifyAndCheckRequest,
  VerifyAndCheckResponse,
  CreateTenantRequest,
  CreateTenantResponse,
  SectorsResponse,
} from '../types/setup-wizard';
import type { SurveyModule } from '../data/setup-wizard-data';

export const getSetupModules = (): Promise<{ modules: SurveyModule[]; total: number }> => {
  return api.get<{ modules: SurveyModule[]; total: number }>('/setup/modules');
};

/**
 * Validation provenance shown as a label on each survey template card
 * (EL-442). `null` means no documented source, so no label is rendered.
 */
export type TemplateValidationStatus =
  | 'academically_validated'
  | 'market_standard'
  | null;

export interface SetupTemplate {
  id: string;
  name: Record<string, string>;
  descriptions: { public_employee?: Record<string, string>; public_employer?: Record<string, string> } | null;
  sourceModuleIds: string[];
  moduleCount: number;
  questionCount: number;
  validationStatus: TemplateValidationStatus;
  sourceUrl: string | null;
  sourceLabel: string | null;
  domain: string | null;
}

export const getSetupTemplates = (): Promise<{ templates: SetupTemplate[] }> => {
  return api.get<{ templates: SetupTemplate[] }>('/setup/templates');
};

export const setupRegister = (data: RegisterSetupRequest): Promise<RegisterSetupResponse> => {
  return api.post<RegisterSetupResponse>('/setup/register', data);
};

export const launchSetup = (data: LaunchSetupRequest): Promise<LaunchSetupResponse> => {
  return api.post<LaunchSetupResponse>('/setup/launch', data);
};

export const createDraftSurvey = (data: CreateDraftSurveyRequest): Promise<CreateDraftSurveyResponse> => {
  return api.post<CreateDraftSurveyResponse>('/setup/create-draft-survey', data);
};

export const sendSetupOtp = (email: string): Promise<{ success: boolean }> => {
  return api.post<{ success: boolean }>('/setup/send-otp', { email });
};

export const verifyAndLaunch = (data: VerifyAndLaunchRequest): Promise<VerifyAndLaunchResponse> => {
  return api.post<VerifyAndLaunchResponse>('/setup/verify-and-launch', data);
};

export const skipAndVerify = (data: SkipAndVerifyRequest): Promise<SkipAndVerifyResponse> => {
  return api.post<SkipAndVerifyResponse>('/setup/skip-and-verify', data);
};

export const setupResume = (token: string): Promise<SetupResumeResponse> => {
  return api.get<SetupResumeResponse>(`/setup/resume?token=${encodeURIComponent(token)}`);
};

export const getSetupConfig = async (tenantId: string): Promise<{ anonymity_threshold: number }> => {
  try {
    return await api.get<{ anonymity_threshold: number }>(`/setup/config?tenant_id=${encodeURIComponent(tenantId)}`);
  } catch {
    return { anonymity_threshold: 5 };
  }
};

export const saveSetupProgress = (step: number, data: Record<string, unknown>): Promise<{ success: boolean }> => {
  return api.post<{ success: boolean }>('/setup/save-progress', { step, data });
};

export const sendVerification = (data: SendVerificationRequest): Promise<SendVerificationResponse> => {
  return api.post<SendVerificationResponse>('/setup/send-verification', data);
};

export const verifyAndCheck = (data: VerifyAndCheckRequest): Promise<VerifyAndCheckResponse> => {
  return api.post<VerifyAndCheckResponse>('/setup/verify-and-check', data);
};

export const createSetupTenant = (data: CreateTenantRequest): Promise<CreateTenantResponse> => {
  return api.post<CreateTenantResponse>('/setup/create-tenant', data);
};

export const fetchSectors = (): Promise<SectorsResponse> => {
  return api.get<SectorsResponse>('/setup/sectors');
};

// === Known Users ===

export type KnownAuthMethod = 'passkey' | 'totp' | 'email';

export interface KnownUser {
  id: string;
  displayName: string;
  hasPasskey: boolean;
  hasTotp: boolean;
  email?: string;
  lastAuthMethod?: KnownAuthMethod;
  lastTenantId?: string;
  lastTenantName?: string;
}

export async function getKnownUsers(): Promise<KnownUser[]> {
  return api.get<KnownUser[]>('/session/known-users');
}

// === Passkey Authentication ===

export async function getPasskeyRegistrationOptions(): Promise<Record<string, unknown>> {
  return api.post('/passkey/registration-options', {});
}

export async function registerPasskey(
  credentialName: string,
  challengeId: string,
  attestationResponse: Record<string, unknown>,
): Promise<CredentialItem> {
  return api.post('/passkey/register', {
    credential_name: credentialName,
    challenge_id: challengeId,
    attestation_response: attestationResponse,
  });
}

export async function getPasskeyAuthenticationOptions(
  email: string,
): Promise<Record<string, unknown>> {
  return api.post('/passkey/authentication-options', { email });
}

export async function authenticatePasskey(
  challengeId: string,
  assertionResponse: Record<string, unknown>,
): Promise<{ temp_token: string; expires_in: number }> {
  return api.post('/passkey/authenticate', {
    challenge_id: challengeId,
    assertion_response: assertionResponse,
  });
}

// === Credential Management ===

export interface CredentialItem {
  id: string;
  type: string;
  name: string;
  enabled: boolean;
  created_at: string;
  last_used_at: string | null;
}

export async function listCredentials(): Promise<CredentialItem[]> {
  return api.get<CredentialItem[]>('/passkey/credentials');
}

export async function renameCredential(id: string, name: string): Promise<CredentialItem> {
  return api.patch<CredentialItem>(`/passkey/credentials/${id}`, { name });
}

export async function toggleCredential(id: string, enabled: boolean): Promise<CredentialItem> {
  return api.patch<CredentialItem>(`/passkey/credentials/${id}`, { enabled });
}

export async function deleteCredential(id: string): Promise<void> {
  return api.delete(`/passkey/credentials/${id}`);
}

// === Active Sessions ===

export interface ActiveSession {
  id: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  last_activity: string | null;
  is_current: boolean;
}

export async function listActiveSessions(): Promise<ActiveSession[]> {
  return api.get<ActiveSession[]>('/session/active-sessions');
}

export async function revokeSession(sessionId: string): Promise<void> {
  return api.delete(`/session/sessions/${sessionId}`);
}

// In-app notification types
export interface InAppNotification {
  id: string;
  type: string;
  category: string;
  title: string;
  body: string;
  metadata: Record<string, unknown>;
  read: boolean;
  created_at: string;
}

export interface NotificationListResponse {
  notifications: InAppNotification[];
  unread_count: number;
}

export interface UnreadCountResponse {
  unread_count: number;
}

// In-app notifications
export const getNotifications = (limit?: number): Promise<NotificationListResponse> => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', String(limit));
  const qs = params.toString();
  return api.get<NotificationListResponse>(`/notifications${qs ? `?${qs}` : ''}`);
};

export const getUnreadCount = (): Promise<UnreadCountResponse> => {
  return api.get<UnreadCountResponse>('/notifications/unread-count');
};

export const markNotificationAsRead = (id: string): Promise<void> => {
  return api.patch<void>(`/notifications/${encodeURIComponent(id)}/read`, {});
};

export const markAllNotificationsAsRead = (): Promise<void> => {
  return api.post<void>('/notifications/mark-all-read', {});
};

// TOTP Authenticator API functions

export type AuthMethods = { methods: { passkey: boolean; totp: boolean } };

export const checkAuthMethods = (email: string): Promise<AuthMethods> => {
  return api.post<AuthMethods>('/session/check-methods', { email });
};

export const checkTotpRequired = (tempToken: string): Promise<{ totp_required: boolean }> => {
  return api.post<{ totp_required: boolean }>('/session/totp-required', { temp_token: tempToken });
};

export const verifyTotpLogin = (email: string, code: string): Promise<{ temp_token: string; expires_in: number }> => {
  return api.post<{ temp_token: string; expires_in: number }>('/session/verify-totp', { email, code });
};

export type TotpSetupOptions = { challenge_id: string; provisioning_uri: string; manual_entry_key: string };

export const getTotpSetupOptions = (): Promise<TotpSetupOptions> => {
  return api.post<TotpSetupOptions>('/totp/setup-options', {});
};

export const verifyTotpSetup = (challengeId: string, code: string): Promise<CredentialItem> => {
  return api.post<CredentialItem>('/totp/verify-setup', { challenge_id: challengeId, code });
};

// Team Leader Management types

export interface TeamLeaderScope {
  field: string;
  values: string[];
}

export interface TeamLeaderAssignment {
  id: string;
  tenant_id: string;
  user_id: string;
  scopes: TeamLeaderScope[];
  parent_team_leader_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface TeamLeaderJWTScope {
  scopes: TeamLeaderScope[];
  inherited_scopes: TeamLeaderScope[];
  depth: number;
}

export interface SubScope {
  user_id: string;
  scopes: TeamLeaderScope[];
}

export interface CreateTeamLeaderInput {
  user_id: string;
  scopes: TeamLeaderScope[];
  parent_team_leader_id?: string | null;
}

export interface UpdateTeamLeaderInput {
  scopes?: TeamLeaderScope[];
  parent_team_leader_id?: string | null;
  /**
   * When true, detaches this team leader from its current parent.
   * Takes precedence over parent_team_leader_id. Use to promote to top-level.
   */
  clear_parent?: boolean;
}

// Team Leader Management API functions

/**
 * Get all team leader assignments for the current tenant
 */
export const getTeamLeaders = (): Promise<TeamLeaderAssignment[]> => {
  return api.get<TeamLeaderAssignment[]>('/team-leaders');
};

/**
 * Get the current user's team leader scope
 * Returns null if the user is not a team leader (404)
 */
export const getMyScope = async (): Promise<TeamLeaderJWTScope | null> => {
  try {
    return await api.get<TeamLeaderJWTScope>('/team-leaders/my-scope');
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
};

/**
 * Get sub-scopes for team leaders reporting to the current user
 */
export const getSubScopes = (): Promise<SubScope[]> => {
  return api.get<SubScope[]>('/team-leaders/sub-scopes');
};

/**
 * Create a new team leader assignment
 */
export const createTeamLeader = (data: CreateTeamLeaderInput): Promise<TeamLeaderAssignment> => {
  return api.post<TeamLeaderAssignment>('/team-leaders', data);
};

/**
 * Update an existing team leader assignment
 */
export const updateTeamLeader = (userId: string, data: UpdateTeamLeaderInput): Promise<TeamLeaderAssignment> => {
  return api.put<TeamLeaderAssignment>(`/team-leaders/${encodeURIComponent(userId)}`, data);
};

/**
 * Delete a team leader assignment
 */
export const deleteTeamLeader = (userId: string): Promise<void> => {
  return api.delete(`/team-leaders/${encodeURIComponent(userId)}`);
};

/**
 * Bulk create team leader assignments
 */
export const bulkCreateTeamLeaders = (assignments: CreateTeamLeaderInput[]): Promise<TeamLeaderAssignment[]> => {
  return api.post<TeamLeaderAssignment[]>('/team-leaders/bulk', assignments);
};

// Team Leader picker helpers

export interface FieldValueCount {
  value: string;
  user_count: number;
}

export interface TeamLeaderFieldKey {
  field: string;
  label: string;
  source: 'profile' | 'metadata';
  is_array: boolean;
  user_count: number;
}

export interface TenantUserSummary {
  user_id: string;
  email: string;
  display_name: string | null;
}

/**
 * List unique metadata values for a profile field within the caller's tenant.
 * Used by the Team Leaders tab to show one row per value (Department, Team, etc.).
 */
export const getTeamLeaderFieldValues = (
  field: string,
): Promise<FieldValueCount[]> => {
  return api.get<FieldValueCount[]>(
    `/team-leader-options/values?field=${encodeURIComponent(field)}`,
  );
};

/**
 * List scopable fields for the Team Leaders admin UI.
 * Canonical fields (department/team/...) plus any custom metadata keys
 * actually present on tenant memberships.
 */
export const getTeamLeaderFieldKeys = (): Promise<TeamLeaderFieldKey[]> => {
  return api.get<TeamLeaderFieldKey[]>('/team-leader-options/keys');
};

/**
 * Search tenant users for the team leader picker (name or email).
 */
export const searchTeamLeaderUsers = (
  search: string,
  limit = 15,
): Promise<TenantUserSummary[]> => {
  const params = new URLSearchParams({
    search,
    limit: String(limit),
  });
  return api.get<TenantUserSummary[]>(
    `/team-leader-options/users?${params.toString()}`,
  );
};

// Bulk import – normalize values

import type {
  NormalizeValuesRequest,
  NormalizeValuesResponse,
} from '../types/import';

/**
 * Resolve raw custom-field values against the tenant's known options.
 * Calls POST /api/users/bulk/import/normalize-values (BFF proxy → user backend).
 */
export const bulkImportNormalizeValues = (
  body: NormalizeValuesRequest,
): Promise<NormalizeValuesResponse> => {
  return api.post<NormalizeValuesResponse>(
    '/users/bulk/import/normalize-values',
    body,
  );
};

// ---------------------------------------------------------------------------
// Tenant Context (HR-admin free-form context attached to a tenant)
// ---------------------------------------------------------------------------

export const getTenantContext = (): Promise<TenantContextDoc> => {
  return api.get<TenantContextDoc>('/tenant-context');
};

export const putTenantContext = (ctx: TenantContextDoc): Promise<TenantContextDoc> => {
  return api.put<TenantContextDoc>('/tenant-context', ctx);
};

export const getTenantContextDimensionValues = (): Promise<DimensionValues> => {
  return api.get<DimensionValues>('/tenant-context/dimension-values');
};

// ---------------------------------------------------------------------------
// Membership Activation (EL-507)
// ---------------------------------------------------------------------------

/**
 * Activate a single INVITED membership.
 * Returns 400 if the membership is not in INVITED status.
 */
export const activateMembership = (membershipId: string): Promise<unknown> =>
  api.post<unknown>(`/dashboard/users/${encodeURIComponent(membershipId)}/activate`, {});

/**
 * Activate all INVITED memberships for the current tenant.
 * Returns the count of activated memberships and any failed membership ids.
 */
export const activateAllPending = (): Promise<{ activated: number; failed: string[] }> =>
  api.post<{ activated: number; failed: string[] }>('/dashboard/users/activate-all-pending', {});

// ---------------------------------------------------------------------------
// Glossary (EL-569)
// ---------------------------------------------------------------------------

/**
 * Fetch the glossary: every module + driver term that carries a definition.
 * Callers without survey access receive an empty list (the BFF degrades
 * gracefully), so the page always renders.
 */
export const getGlossary = (): Promise<GlossaryResponse> =>
  api.get<GlossaryResponse>('/glossary');

export { ApiError };

/**
 * Get this tenant's statutory compliance obligations (RI&E, PSRA — EL-1066).
 *
 * Every obligation is returned, including the ones nobody has configured
 * (`status: 'not_configured'`, `days_remaining: null`). Reading never writes.
 */
export const getComplianceReports = (): Promise<ComplianceReportsResponse> => {
  return api.get<ComplianceReportsResponse>('/compliance/reports');
};

/**
 * Record (or clear) one obligation's configuration — EL-1066.
 *
 * A full replacement, not a patch: the backend clears every field the payload
 * omits, and `{}` returns the obligation to `not_configured`. Callers must
 * therefore send the complete intended state, including the fields they did
 * not touch — sending only the edited field would silently erase the rest.
 *
 * `due_date` is a plain `YYYY-MM-DD` calendar date, never a timestamp: a
 * statutory deadline is a day in Europe/Brussels, and sending an instant would
 * let a timezone shift move a legal deadline by one day.
 */
export const saveComplianceReport = (
  reportType: ComplianceReportType,
  config: ComplianceReportConfig,
): Promise<ComplianceReport> => {
  return api.put<ComplianceReport>(`/compliance/reports/${reportType}`, config);
};
